import { useEffect, useState } from 'react'
import {
  LineChart as RechartLineChart,
  Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ReferenceArea, ReferenceLine, ResponsiveContainer,
} from 'recharts'
import type { WeightTrajectory, WeightReadingPoint, WeightContext } from '../lib/weightDebug'
import type { WindowSpec, WindowUnit } from '../lib/weightDebug'
import {
  medianWeightInWindow, fetchWeightContext, TARE_LOOKBACK_S,
  DEFAULT_WINDOW, UNIT_SECONDS, MAX_WINDOW_PAD_S, LB_PER_GRAM, poundTicks,
} from '../lib/weightDebug'

const TICK_STYLE = { fill: '#78716c', fontFamily: 'JetBrains Mono, monospace', fontSize: 9 }
const CELL_COLORS = ['#355E3B', '#D8613C', '#F0C070', '#9B3C3C', '#7a5a52', '#a07a72']
const TARE_COLOR = '#5c3e35' // matches the timeline's tare glyph
const LOGGED_COLOR = '#2f6f8f'
const WINDOW_KEY = 'kodiak-weight-window'
const Y_RANGE_KEY = 'kodiak-weight-y-range'

// Manual y-axis bounds in lb, as typed ('' = auto for that end).
interface YRange { min: string; max: string }

function storedYRange(): YRange {
  try {
    const v = JSON.parse(window.localStorage.getItem(Y_RANGE_KEY) ?? 'null') as Partial<YRange> | null
    if (v && typeof v === 'object') {
      return { min: typeof v.min === 'string' ? v.min : '', max: typeof v.max === 'string' ? v.max : '' }
    }
  } catch { /* junk: fall through */ }
  return { min: '', max: '' }
}

const parseLb = (s: string): number | null => {
  const n = s.trim() === '' ? NaN : Number(s)
  return Number.isFinite(n) ? n : null
}

function LbInput({ value, onChange, label }: { value: string; onChange: (v: string) => void; label: string }) {
  return (
    <input
      type="number"
      step="any"
      value={value}
      placeholder="auto"
      onChange={e => onChange(e.target.value)}
      onFocus={e => e.target.select()}
      aria-label={label}
      className="h-6 w-16 rounded border border-stone-200 bg-white px-1.5 text-right font-mono text-[11px] text-stone-800 placeholder:text-stone-300"
    />
  )
}

function storedWindow(): WindowSpec {
  try {
    const v = JSON.parse(window.localStorage.getItem(WINDOW_KEY) ?? 'null') as Partial<WindowSpec> | null
    const unit = (u: unknown): WindowUnit => (u === 'm' || u === 'h' ? u : 's')
    const n = (x: unknown) => (typeof x === 'number' && x >= 0 ? x : 0)
    if (v && typeof v === 'object') {
      return { before: n(v.before), beforeUnit: unit(v.beforeUnit), after: n(v.after), afterUnit: unit(v.afterUnit) }
    }
  } catch { /* old preset key or junk: fall through */ }
  return DEFAULT_WINDOW
}

const padSeconds = (n: number, u: WindowUnit) => Math.min(MAX_WINDOW_PAD_S, n * UNIT_SECONDS[u])

// One side of the window: a number and a unit.
function PadInput({ value, unit, onChange, label }: {
  value: number
  unit: WindowUnit
  onChange: (value: number, unit: WindowUnit) => void
  label: string
}) {
  const field = 'h-6 rounded border border-stone-200 bg-white font-mono text-[11px] text-stone-800'
  return (
    <span className="inline-flex items-center gap-1">
      <input
        type="number"
        min={0}
        step="any"
        value={value}
        onChange={e => onChange(Math.max(0, Number(e.target.value) || 0), unit)}
        onFocus={e => e.target.select()}
        aria-label={`${label} amount`}
        className={`${field} w-14 px-1.5 text-right`}
      />
      <select
        value={unit}
        onChange={e => onChange(value, e.target.value as WindowUnit)}
        aria-label={`${label} unit`}
        className={`${field} px-1`}
      >
        <option value="s">seconds</option>
        <option value="m">minutes</option>
        <option value="h">hours</option>
      </select>
    </span>
  )
}

// Signed elapsed time: seconds for short spans, m:ss once the axis is minutes wide.
function fmtElapsed(dt: number, span: number): string {
  if (span <= 120) return `${dt.toFixed(1)}s`
  const sign = dt < 0 ? '−' : ''
  const a = Math.abs(dt)
  if (a >= 3600) return `${sign}${Math.floor(a / 3600)}h${String(Math.floor((a % 3600) / 60)).padStart(2, '0')}`
  return `${sign}${Math.floor(a / 60)}:${String(Math.floor(a % 60)).padStart(2, '0')}`
}

// "41 s" / "12 min" / "3.2 h" for the last-tare readout.
function fmtAgo(s: number): string {
  if (s < 90) return `${Math.round(s)} s`
  if (s < 5400) return `${Math.round(s / 60)} min`
  return `${(s / 3600).toFixed(1)} h`
}

interface Frame {
  index: number
  t: number
  chosen: boolean
}

function fmtG(g: number | null): string {
  if (g == null) return '—'
  return `${Math.round(g)} g · ${(g * LB_PER_GRAM).toFixed(2)} lb`
}

interface Row {
  t: number
  weight: number | null
  loggedBefore: number | null
  loggedAfter: number | null
  smoothed: number | null
  outlier: boolean
  used: boolean
  [cell: string]: number | boolean | null
}

function ReadingDot(props: {
  cx?: number
  cy?: number
  index?: number
  payload?: WeightReadingPoint
}) {
  const { cx, cy, index, payload } = props
  // index, not t: the rows joining the 5 s log to this trace repeat its end times.
  const key = `dot-${index ?? `${cx},${cy}`}`
  if (cx == null || cy == null || !payload || payload.weight == null) return <g key={key} />
  if (payload.outlier) {
    return (
      <path
        key={key}
        d={`M${cx - 3},${cy - 3} L${cx + 3},${cy + 3} M${cx - 3},${cy + 3} L${cx + 3},${cy - 3}`}
        stroke="#D8613C"
        strokeWidth={1.5}
      />
    )
  }
  if (payload.used) {
    return <circle key={key} cx={cx} cy={cy} r={3} fill="#F0C070" stroke="#9B3C3C" strokeWidth={1} />
  }
  return <circle key={key} cx={cx} cy={cy} r={2} fill="#355E3B" />
}

function LegendItem({ swatch, label }: { swatch: React.ReactNode; label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-stone-500">
      {swatch}
      {label}
    </span>
  )
}

export default function WeightTrajectoryChart({
  trajectory,
  captureStart,
  captureEnd,
  frames = [],
  hostname,
  captureId,
}: {
  trajectory: WeightTrajectory
  captureStart?: number | null
  captureEnd?: number | null
  frames?: Frame[]
  hostname?: string | null
  captureId?: string
}) {
  const { readings, stable, stableStart, stableEnd, stableStddev, reportedWeight, cellKeys } =
    trajectory
  const [showCells, setShowCells] = useState(false)

  const minT = readings[0].t
  const maxT = readings[readings.length - 1].t
  // The capture's own span: its readings AND the capture window AND every frame.
  const frameTs = frames.map(f => f.t)
  const spanMin = Math.min(minT, captureStart ?? minT, ...frameTs)
  const spanMax = Math.max(maxT, captureEnd ?? maxT, ...frameTs)
  // Elapsed labels are relative to capture start when known (so 0.0s = capture start).
  const refT = captureStart ?? minT

  // View window: the capture span, or the span padded either side. The choice
  // sticks across captures so labelling a run keeps the same zoom.
  const [win, setWin] = useState(storedWindow)
  const [yRange, setYRange] = useState(storedYRange)
  useEffect(() => { window.localStorage.setItem(Y_RANGE_KEY, JSON.stringify(yRange)) }, [yRange])
  useEffect(() => { window.localStorage.setItem(WINDOW_KEY, JSON.stringify(win)) }, [win])
  // Typing "120" passes through 1 and 12; let it settle before refetching.
  const [settled, setSettled] = useState(win)
  useEffect(() => {
    const id = window.setTimeout(() => setSettled(win), 400)
    return () => window.clearTimeout(id)
  }, [win])
  const padBefore = padSeconds(settled.before, settled.beforeUnit)
  const padAfter = padSeconds(settled.after, settled.afterUnit)
  const wide = padBefore > 0 || padAfter > 0
  const domainMin = spanMin - padBefore
  const domainMax = spanMax + padAfter

  // Device events (5 s weight log, tares, other captures) for the view window.
  const [ctx, setCtx] = useState<WeightContext | null>(null)
  const [ctxError, setCtxError] = useState<string | null>(null)
  useEffect(() => {
    if (!hostname) return
    let cancelled = false
    setCtxError(null)
    fetchWeightContext(hostname, domainMin, domainMax, refT, wide)
      .then(c => { if (!cancelled) setCtx(c) })
      .catch((e: Error) => { if (!cancelled) { setCtx(null); setCtxError(e.message) } })
    return () => { cancelled = true }
  }, [hostname, domainMin, domainMax, refT, wide])

  const tares = ctx?.tares ?? []
  const lastTare = [...tares].reverse().find(m => m.t <= refT) ?? null
  const taresInView = tares.filter(m => m.t >= domainMin && m.t <= domainMax)
  const calsInView = (ctx?.calibrations ?? []).filter(t => t >= domainMin && t <= domainMax)
  const otherCaptures = (ctx?.captures ?? []).filter(c => c.captureId !== captureId)
  // The 5 s log only where the capture's dense readings aren't.
  const logged = !wide ? [] : (ctx?.readings ?? []).filter(r => r.t < minT || r.t > maxT)

  // Candidate what-if window — index-based over the discrete readings so the
  // thumbs snap to actual samples. Initialized to the device stable window.
  const initStart =
    stableStart != null
      ? Math.max(0, readings.findIndex(r => r.t >= stableStart))
      : 0
  const initEnd =
    stableEnd != null
      ? (() => {
          const i = readings.map(r => r.t).reverse().findIndex(t => t <= stableEnd)
          return i < 0 ? readings.length - 1 : readings.length - 1 - i
        })()
      : readings.length - 1
  const [startIdx, setStartIdx] = useState(initStart)
  const [endIdx, setEndIdx] = useState(initEnd)
  const lo = Math.min(startIdx, endIdx)
  const hi = Math.max(startIdx, endIdx)
  const candStart = readings[lo].t
  const candEnd = readings[hi].t
  const candWeight = medianWeightInWindow(readings, candStart, candEnd)
  const delta = candWeight != null && reportedWeight != null ? candWeight - reportedWeight : null

  const data: Row[] = readings.map(r => {
    const row: Row = { t: r.t, weight: r.weight, loggedBefore: null, loggedAfter: null, smoothed: r.smoothed, outlier: r.outlier, used: r.used }
    for (const ch of cellKeys) row[`cell_${ch}`] = r.cells[ch]?.weight ?? null
    return row
  })
  if (logged.length > 0) {
    // Two series, before and after the capture, each joined to the capture
    // trace's end so the line reads as one without bridging the capture span.
    const first = readings[0]
    const last = readings[readings.length - 1]
    const blank = { weight: null, loggedBefore: null, loggedAfter: null, smoothed: null, outlier: false, used: false }
    for (const r of logged) {
      data.push(r.t < minT ? { ...blank, t: r.t, loggedBefore: r.weight } : { ...blank, t: r.t, loggedAfter: r.weight })
    }
    data.push({ ...blank, t: first.t, loggedBefore: first.weight })
    data.push({ ...blank, t: last.t, loggedAfter: last.weight })
    data.sort((a, b) => a.t - b.t)
  }

  const span = domainMax - domainMin
  // y-axis in pounds: ticks at round lb over everything drawn.
  const yValues: number[] = []
  for (const row of data) {
    for (const k of ['weight', 'loggedBefore', 'loggedAfter', 'smoothed', ...(showCells ? cellKeys.map(ch => `cell_${ch}`) : [])]) {
      const v = row[k]
      if (typeof v === 'number') yValues.push(v)
    }
  }
  if (reportedWeight != null) yValues.push(reportedWeight)
  // Manual bounds override either end; the other end stays auto. An inverted
  // range is ignored rather than drawing an empty chart.
  const autoTicks = poundTicks(yValues)
  const autoLo = autoTicks.ticks[0]
  const autoHi = autoTicks.ticks[autoTicks.ticks.length - 1]
  const manLo = parseLb(yRange.min)
  const manHi = parseLb(yRange.max)
  const yLo = manLo != null ? manLo / LB_PER_GRAM : autoLo
  const yHi = manHi != null ? manHi / LB_PER_GRAM : autoHi
  const yManual = (manLo != null || manHi != null) && yLo != null && yHi != null
  const yInverted = yManual && yLo >= yHi
  const yTicks = yManual && !yInverted
    ? (() => {
        const t = poundTicks([yLo, yHi])
        return { ...t, ticks: t.ticks.filter(g => g >= yLo - 1e-6 && g <= yHi + 1e-6) }
      })()
    : autoTicks
  const yDomain: [number, number] | ['auto', 'auto'] = yManual && !yInverted
    ? [yLo, yHi]
    : autoTicks.ticks.length ? [autoLo, autoHi] : ['auto', 'auto']
  const tickFormatter = (t: number) => fmtElapsed(t - refT, span)

  function resetWindow() {
    setStartIdx(initStart)
    setEndIdx(initEnd)
  }

  return (
    <div className="rounded-lg bg-stone-50 border border-stone-100 px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between">
        <span className="font-mono text-[9px] tracking-[0.2em] uppercase text-stone-500">
          Weight trajectory
        </span>
        <button
          onClick={() => setShowCells(c => !c)}
          className="font-mono text-[9px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900 cursor-pointer"
        >
          {showCells ? 'hide per-cell' : 'show per-cell'}
        </button>
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] text-stone-500">
        {/* View window: N before the capture to N after it (0 / 0 = capture only) */}
        {hostname && (<>
          <PadInput label="before" value={win.before} unit={win.beforeUnit}
            onChange={(before, beforeUnit) => setWin(w => ({ ...w, before, beforeUnit }))} />
          <span>before to</span>
          <PadInput label="after" value={win.after} unit={win.afterUnit}
            onChange={(after, afterUnit) => setWin(w => ({ ...w, after, afterUnit }))} />
          <span>after</span>
          {wide && (
            <button
              onClick={() => setWin(DEFAULT_WINDOW)}
              className="ml-1 text-[9px] tracking-[0.12em] uppercase text-stone-400 hover:text-stone-900 cursor-pointer"
            >
              capture only
            </button>
          )}
          {(win.before * UNIT_SECONDS[win.beforeUnit] > MAX_WINDOW_PAD_S
            || win.after * UNIT_SECONDS[win.afterUnit] > MAX_WINDOW_PAD_S) && (
            <span className="text-gold">capped at {MAX_WINDOW_PAD_S / 3600} h a side</span>
          )}
        </>)}

        {/* y-axis range in lb; blank = auto for that end */}
        <span className="ml-auto inline-flex items-center gap-1">
          <span>y</span>
          <LbInput label="y min (lb)" value={yRange.min} onChange={min => setYRange(r => ({ ...r, min }))} />
          <span>to</span>
          <LbInput label="y max (lb)" value={yRange.max} onChange={max => setYRange(r => ({ ...r, max }))} />
          <span>lb</span>
          {(yRange.min !== '' || yRange.max !== '') && (
            <button
              onClick={() => setYRange({ min: '', max: '' })}
              className="ml-1 text-[9px] tracking-[0.12em] uppercase text-stone-400 hover:text-stone-900 cursor-pointer"
            >
              auto
            </button>
          )}
          {yInverted && <span className="text-gold">min ≥ max, using auto</span>}
        </span>
      </div>

      <ResponsiveContainer width="100%" height={260}>
        <RechartLineChart data={data} margin={{ top: 10, right: 8, bottom: 4, left: 4 }}>
          <CartesianGrid stroke="#e7e5e4" vertical={false} />
          <XAxis
            dataKey="t"
            type="number"
            domain={[domainMin, domainMax]}
            allowDataOverflow
            scale="linear"
            tickFormatter={tickFormatter}
            tick={TICK_STYLE}
            tickLine={false}
            axisLine={false}
            minTickGap={30}
          />
          <YAxis
            tick={TICK_STYLE}
            ticks={yTicks.ticks}
            tickFormatter={yTicks.fmt}
            tickLine={false}
            axisLine={false}
            width={40}
            domain={yDomain}
            allowDataOverflow
            label={{ value: 'lb', position: 'insideTopLeft', offset: 0, dy: -10, style: { ...TICK_STYLE } }}
          />
          <Tooltip
            contentStyle={{ background: '#fff', border: '1px solid #e7e5e4', fontFamily: 'JetBrains Mono, monospace', fontSize: 10, borderRadius: 6 }}
            labelStyle={{ color: '#78716c', marginBottom: 4 }}
            formatter={(v: number, name: string) => [v == null ? '—' : `${(v * LB_PER_GRAM).toFixed(2)} lb`, name]}
            labelFormatter={(t: number) => tickFormatter(t)}
            isAnimationActive={false}
          />

          {/* Capture window (behind everything) */}
          {captureStart != null && captureEnd != null && (
            <ReferenceArea
              x1={captureStart}
              x2={captureEnd}
              fill="#78716c"
              fillOpacity={0.06}
              stroke="#78716c"
              strokeOpacity={0.25}
              strokeDasharray="2,2"
            />
          )}
          {/* Device stable window */}
          {stableStart != null && stableEnd != null && (
            <ReferenceArea
              x1={stableStart}
              x2={stableEnd}
              fill={stable ? '#355E3B' : '#F0C070'}
              fillOpacity={0.12}
              stroke={stable ? '#355E3B' : '#F0C070'}
              strokeOpacity={0.5}
            />
          )}
          {/* Candidate what-if window */}
          <ReferenceArea x1={candStart} x2={candEnd} fill="#9B3C3C" fillOpacity={0.08} stroke="#9B3C3C" strokeOpacity={0.4} />
          {/* Image frame markers — chosen frame highlighted */}
          {frames.map(f => (
            <ReferenceLine
              key={`frame-${f.index}`}
              x={f.t}
              stroke={f.chosen ? '#F0C070' : '#d6d3d1'}
              strokeWidth={f.chosen ? 1.5 : 1}
              strokeOpacity={f.chosen ? 0.95 : 0.6}
              strokeDasharray={f.chosen ? undefined : '2,2'}
              label={f.chosen ? { value: `▲${f.index}`, position: 'top', fontSize: 8, fill: '#9B3C3C' } : undefined}
            />
          ))}
          {/* Other captures, calibrations and tares from the device event log */}
          {otherCaptures.map(c => (
            <ReferenceLine
              key={`cap-${c.captureId}`}
              x={c.t}
              stroke="#78716c"
              strokeOpacity={0.5}
              strokeDasharray="4,3"
              label={{ value: 'C', position: 'insideBottomRight', fontSize: 8, fill: '#78716c' }}
            />
          ))}
          {calsInView.map(t => (
            <ReferenceLine
              key={`cal-${t}`}
              x={t}
              stroke="#355E3B"
              strokeWidth={1.5}
              label={{ value: 'cal', position: 'insideBottomRight', fontSize: 8, fill: '#355E3B' }}
            />
          ))}
          {taresInView.map(m => (
            <ReferenceLine
              key={`tare-${m.t}`}
              x={m.t}
              stroke={TARE_COLOR}
              strokeWidth={1.5}
              label={{
                value: `T${m.grams != null ? ` ${m.grams >= 0 ? '+' : ''}${(m.grams * LB_PER_GRAM).toFixed(2)}` : ''}`,
                position: 'insideTopLeft',
                fontSize: 8,
                fontWeight: 600,
                fill: TARE_COLOR,
              }}
            />
          ))}
          {reportedWeight != null && (
            <ReferenceLine y={reportedWeight} stroke="#355E3B" strokeDasharray="3,3" strokeOpacity={0.6} />
          )}

          {showCells &&
            cellKeys.map((ch, i) => (
              <Line
                key={ch}
                type="monotone"
                dataKey={`cell_${ch}`}
                name={`cell ${ch}`}
                stroke={CELL_COLORS[i % CELL_COLORS.length]}
                strokeWidth={1}
                strokeOpacity={0.7}
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          <Line
            type="monotone"
            dataKey="smoothed"
            name="smoothed"
            stroke="#a07a72"
            strokeWidth={1}
            strokeOpacity={0.5}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
          {logged.length > 0 &&
            (['loggedBefore', 'loggedAfter'] as const).map(k => (
              <Line
                key={k}
                type="linear"
                dataKey={k}
                name="5 s log"
                stroke={LOGGED_COLOR}
                strokeWidth={1}
                strokeOpacity={0.7}
                dot={logged.length <= 200 ? { r: 1.5, fill: LOGGED_COLOR, strokeWidth: 0 } : false}
                activeDot={{ r: 3 }}
                connectNulls
                isAnimationActive={false}
              />
            ))}
          <Line
            type="monotone"
            dataKey="weight"
            name="weight"
            stroke="#1a1310"
            strokeWidth={1.5}
            dot={ReadingDot}
            activeDot={{ r: 4 }}
            connectNulls
            isAnimationActive={false}
          />
        </RechartLineChart>
      </ResponsiveContainer>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-3 gap-y-1 font-mono text-[8px]">
        <LegendItem label="weight" swatch={<span className="w-3 h-0.5 bg-[#1a1310]" />} />
        <LegendItem label="smoothed" swatch={<span className="w-3 h-0.5 bg-[#a07a72]" />} />
        <LegendItem label="used for weight" swatch={<span className="w-2 h-2 rounded-full bg-gold border border-bear" />} />
        <LegendItem label="reading" swatch={<span className="w-2 h-2 rounded-full bg-pine" />} />
        <LegendItem label="outlier (device 2σ)" swatch={<span className="text-coral font-bold leading-none">✕</span>} />
        <LegendItem label="stable window" swatch={<span className="w-3 h-2 bg-pine/20 border border-pine/50" />} />
        <LegendItem label="capture window" swatch={<span className="w-3 h-2 bg-stone-400/10 border border-dashed border-stone-400/50" />} />
        {wide && (
          <LegendItem label="5 s device log" swatch={<span className="w-3 h-0.5" style={{ background: LOGGED_COLOR }} />} />
        )}
        <LegendItem label="tare (T ± lb removed)" swatch={<span className="w-0.5 h-2.5" style={{ background: TARE_COLOR }} />} />
        {wide && (
          <LegendItem label="other capture" swatch={<span className="w-px h-2.5 border-l border-dashed border-stone-400" />} />
        )}
        <LegendItem label="image frame / chosen" swatch={<span className="flex items-center"><span className="w-px h-2.5 bg-stone-300" /><span className="w-px h-2.5 bg-gold ml-0.5" /></span>} />
      </div>

      {/* Tare context: usually minutes away, so say when the last one was */}
      {hostname && (
        <div className="font-mono text-[9px] text-stone-500">
          {ctxError ? (
            <span className="text-coral">device events failed: {ctxError}</span>
          ) : ctx == null ? (
            <span className="text-stone-400">loading device events…</span>
          ) : lastTare ? (
            <>
              last tare <span className="text-stone-700">{fmtAgo(refT - lastTare.t)}</span> before capture
              {lastTare.grams != null && <> · {lastTare.grams >= 0 ? '+' : ''}{(lastTare.grams * LB_PER_GRAM).toFixed(2)} lb removed</>}
              {taresInView.length > 0 && <> · {taresInView.length} in view</>}
            </>
          ) : (
            <span className="text-gold">no tare in the {Math.round(TARE_LOOKBACK_S / 3600)} h before capture</span>
          )}
        </div>
      )}

      {/* Stability summary */}
      {stableStart != null && stableEnd != null ? (
        <div className="font-mono text-[9px] text-stone-500">
          {stable ? (
            <span className="text-pine">stable window</span>
          ) : (
            <span className="text-gold">unstable window</span>
          )}
          {stableStddev != null && <> · stddev {Math.round(stableStddev)} g</>}
          {' · device weight '}
          <span className="text-stone-700">{fmtG(reportedWeight)}</span>
        </div>
      ) : (
        <div className="font-mono text-[9px] text-coral">
          No stable window found — device weight is the median of all in-window readings
          {' · '}
          <span className="text-stone-700">{fmtG(reportedWeight)}</span>
        </div>
      )}

      {/* What-if window controls */}
      <div className="space-y-1.5 pt-1">
        <div className="flex items-center justify-between">
          <span className="font-mono text-[9px] tracking-[0.15em] uppercase text-stone-500">
            What-if window
          </span>
          <button
            onClick={resetWindow}
            className="font-mono text-[9px] tracking-[0.12em] uppercase text-stone-500 hover:text-stone-900 cursor-pointer"
          >
            reset to device pick
          </button>
        </div>
        <div className="relative h-4">
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded bg-stone-200" />
          <div
            className="absolute top-1/2 -translate-y-1/2 h-1 rounded bg-bear/40"
            style={{
              left: `${(lo / (readings.length - 1)) * 100}%`,
              right: `${100 - (hi / (readings.length - 1)) * 100}%`,
            }}
          />
          <input
            type="range"
            min={0}
            max={readings.length - 1}
            value={startIdx}
            onChange={e => setStartIdx(Number(e.target.value))}
            className="dual-range"
            aria-label="window start"
          />
          <input
            type="range"
            min={0}
            max={readings.length - 1}
            value={endIdx}
            onChange={e => setEndIdx(Number(e.target.value))}
            className="dual-range"
            aria-label="window end"
          />
        </div>
        <div className="flex items-baseline justify-between font-mono text-[10px]">
          <span className="text-stone-500">
            {(candStart - refT).toFixed(1)}s–{(candEnd - refT).toFixed(1)}s
          </span>
          <span className="text-bear font-semibold">{fmtG(candWeight)}</span>
        </div>
        {delta != null && Math.abs(delta) >= 1 && (
          <div className="text-right font-mono text-[9px] text-stone-400">
            {delta > 0 ? '+' : ''}{Math.round(delta)} g vs device
          </div>
        )}
      </div>
    </div>
  )
}
